import React, { useEffect, useState } from "react";

const TermsOfService: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleMobileMenuToggle = () => setMobileMenuOpen((open) => !open);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-md py-4 px-6 md:px-12 fixed w-full z-[1000] top-0">
        <nav className="container mx-auto flex justify-between items-center">
          <a
            href="/"
            className="text-2xl font-bold text-brand-dark-blue rounded-md p-2 hover:bg-gray-100 transition-colors"
          >
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-10"
            />
          </a>
          <div className="hidden md:flex space-x-6">
            <a
              href="/"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
            >
              Home
            </a>
            <a
              href="/login"
              className="text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md px-4 py-2 ml-4 shadow-md hover:scale-105 duration-200"
            >
              Log In
            </a>
          </div>
          <button
            className="md:hidden text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-dark-blue rounded-md p-2"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </nav>
        <div
          className={`md:hidden bg-white py-2 mt-2 border-t border-gray-200${
            mobileMenuOpen ? "" : " hidden"
          }`}
        >
          <a
            href="/"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
          >
            Home
          </a>
          <a
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md mx-2 mt-2 shadow-md text-center"
          >
            Log In
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <section className="bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green text-white py-20 px-6 md:px-12 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Terms of Service
            </h1>
            <p className="text-xl opacity-90">Last updated: January 2025</p>
          </div>
        </section>

        <section className="py-16 px-6 md:px-12 bg-white">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Agreement to Terms
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  By accessing and using Amblyfy, you agree to be bound by these
                  Terms of Service ("Terms"). If you disagree with any part, you
                  may not access the service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Description of Service
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Amblyfy is a habit-tracking and motivation app designed for
                  children, managed by their parents or guardians. It helps
                  families create routines, set goals, track progress, and
                  celebrate achievements. Amblyfy is not a medical app and does
                  not provide medical advice, diagnosis, or treatment.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Routine and goal tracking with progress logs</li>
                  <li>Reminders, schedules, and rewards features</li>
                  <li>Mobile and web applications</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  User Accounts and Registration
                </h2>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    To use our services, you must create an account and provide
                    accurate, complete information. You are responsible for:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>
                      Maintaining the confidentiality of your account
                      credentials
                    </li>
                    <li>All activities that occur under your account</li>
                    <li>Notifying us immediately of any unauthorized use</li>
                    <li>
                      Ensuring your account information remains accurate and
                      up-to-date
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Acceptable Use
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You agree to use Amblyfy only for lawful purposes and in
                  accordance with these Terms. You agree not to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    Use the service for any illegal or unauthorized purpose
                  </li>
                  <li>
                    Attempt to gain unauthorized access to our systems or other
                    users' accounts
                  </li>
                  <li>Interfere with or disrupt the service or servers</li>
                  <li>Enter or request sensitive health or medical history</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>
                    Use the service to harm minors or vulnerable individuals
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  No Medical Use
                </h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Important:</strong> Amblyfy is a general-purpose
                    habit tracker for families. It is not a medical device and
                    does not provide medical advice, diagnosis, or treatment.
                  </p>
                </div>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Amblyfy should not be used for medical decisions</li>
                  <li>Do not enter health or medical history into the app</li>
                  <li>
                    Consult a qualified professional for any health-related
                    questions
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Privacy and Data Protection
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Your privacy is important to us. Our collection and use of
                  personal information is governed by our Privacy Policy, which
                  is incorporated by reference.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Intellectual Property
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The service and its original content, features, and
                  functionality are owned by Amblyfy and are protected by
                  international copyright, trademark, patent, trade secret, and
                  other intellectual property laws.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You retain ownership of any content you submit, but grant us a
                  license to use, store, and display that content in connection
                  with providing our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Subscription and Payment Terms
                </h2>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    Some features of our service may require a subscription. By
                    subscribing, you agree to:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>Pay all fees associated with your subscription</li>
                    <li>Provide accurate billing information</li>
                    <li>
                      Authorize recurring charges for subscription renewals
                    </li>
                    <li>
                      Cancel your subscription before the renewal date to avoid
                      charges
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Limitation of Liability
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  To the maximum extent permitted by law, Amblyfy shall not be
                  liable for any indirect, incidental, special, consequential,
                  or punitive damages, including without limitation, loss of
                  profits, data, use, goodwill, or other intangible losses
                  resulting from your use of the service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Disclaimers
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The service is provided on an "as is" and "as available"
                  basis. We make no warranties, express or implied, including
                  but not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    Warranties of merchantability or fitness for a particular
                    purpose
                  </li>
                  <li>
                    Warranties that the service will be uninterrupted or
                    error-free
                  </li>
                  <li>
                    Warranties regarding the accuracy or reliability of any
                    information
                  </li>
                  <li>Warranties that defects will be corrected</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Termination
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We may terminate or suspend your account and access to the
                  service immediately, without prior notice, for any reason,
                  including breach of these Terms. Upon termination, your right
                  to use the service will cease immediately.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Governing Law
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed by and construed in accordance
                  with the laws of Latvia, without regard to its
                  conflict of law provisions.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Changes to Terms
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these Terms at any time. We
                  will notify users of any material changes by posting the new
                  Terms on our website and updating the "Last updated" date.
                  Your continued use of the service after such changes
                  constitutes acceptance of the new Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Contact Information
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions about these Terms of Service, please
                  contact us at:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-gray-700">
                    <strong>Email:</strong> laila@amblyfy.com
                    <br />
                    <strong>Address:</strong> Lastādijas 12-k3, Rīga, Latvia
                    <br />
                    
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          className="fixed bottom-8 right-8 bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 focus:outline-none focus:ring-4 focus:ring-brand-cyan/50 transition-all duration-300 transform backdrop-blur-sm"
          onClick={handleBackToTop}
          aria-label="Back to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 w-full">
        <div className="w-full px-4">
          <div className="max-w-7xl mx-auto flex flex-col items-center space-y-4">
            <p className="text-gray-600">
              &copy; 2025 Amblyfy. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="/privacy-policy"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-brand-dark-blue font-medium"
              >
                Terms of Service
              </a>
              <a
                href="/evidence-statement"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Evidence Statement
              </a>
              <a
                href="/activity-tracking-guide"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Activity Tracking Guide
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default TermsOfService;
